import { useCallback, useState } from "react";
import { reorderLogisticsRouteStops, type LogiStop } from "@/lib/logistics";

export function useLogisticsRouteStopReorder(
  routeId: string | null,
  stops: LogiStop[],
  reload: () => Promise<void>
) {
  const [reorderBusy, setReorderBusy] = useState(false);
  const [reorderError, setReorderError] = useState<string | null>(null);
  const [dragStopId, setDragStopId] = useState<string | null>(null);

  const commitOrder = useCallback(
    async (orderedIds: string[]) => {
      if (!routeId) return;
      setReorderBusy(true);
      setReorderError(null);
      const res = await reorderLogisticsRouteStops({
        route_id: routeId,
        ordered_stop_ids: orderedIds,
      });
      setReorderBusy(false);
      if (!res.ok) {
        setReorderError(res.error);
        return;
      }
      await reload();
    },
    [routeId, reload]
  );

  const moveStop = useCallback(
    (stopId: string, direction: -1 | 1) => {
      const ids = stops.map((s) => s.id);
      const idx = ids.indexOf(stopId);
      if (idx < 0) return;
      const target = idx + direction;
      if (target < 0 || target >= ids.length) return;
      const next = [...ids];
      const [removed] = next.splice(idx, 1);
      next.splice(target, 0, removed!);
      void commitOrder(next);
    },
    [stops, commitOrder]
  );

  const dropOnStop = useCallback(
    (targetStopId: string) => {
      if (!dragStopId || dragStopId === targetStopId) return;
      const ids = stops.map((s) => s.id);
      const from = ids.indexOf(dragStopId);
      const to = ids.indexOf(targetStopId);
      if (from < 0 || to < 0) return;
      const next = [...ids];
      const [removed] = next.splice(from, 1);
      next.splice(to, 0, removed!);
      setDragStopId(null);
      void commitOrder(next);
    },
    [dragStopId, stops, commitOrder]
  );

  return {
    reorderBusy,
    reorderError,
    dragStopId,
    setDragStopId,
    moveStop,
    dropOnStop,
    canReorder: stops.length > 1,
  };
}
